// src/components/admin/subject/SubjectMatrix.js
// Admin Subjects — category-first accordion, full tutor visibility, no horizontal scroll
import React, { useState, useEffect, useMemo, useRef } from "react";
import { db } from "../../../firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  addDoc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import {
  FiSearch,
  FiPlus,
  FiTrash2,
  FiArrowUp,
  FiArrowDown,
  FiX,
  FiChevronRight,
  FiEdit2,
} from "react-icons/fi";
import "./SubjectMatrix.css";

function normalizeCategoryId(name) {
  return (name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

export default function SubjectMatrix() {
  /* ─── State ─────────────────────────────────────────────────────────── */
  const [tutors, setTutors] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [tutorFilter, setTutorFilter] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tempName, setTempName] = useState("");

  const [showAddRootModal, setShowAddRootModal] = useState(false);
  const [newRootName, setNewRootName] = useState("");
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [addSubjectRoot, setAddSubjectRoot] = useState("");
  const [newSubjectName, setNewSubjectName] = useState("");

  // UI: open categories (default all closed), expanded edit panels
  const [openCategories, setOpenCategories] = useState(new Set());
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());

  const searchInputRef = useRef(null);
  const syncInFlightRef = useRef(false);

  /* ─── Firestore: Tutors ──────────────────────────────────────────────── */
  useEffect(() => {
    const fetchTutors = async () => {
      try {
        const q = query(collection(db, "users"), where("role", "==", "tutor"));
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map((d) => ({
          uid: d.id,
          name: d.data().name || "Unknown",
          status: d.data().status || "active",
          subjects: d.data().subjects || [],
          ...d.data(),
        }));
        list.sort((a, b) => a.name.localeCompare(b.name));
        setTutors(list);
      } catch (err) {
        console.error("Error fetching tutors:", err);
      }
    };
    fetchTutors();
  }, []);

  /* ─── Firestore: Categories ──────────────────────────────────────────── */
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, "subject_categories")),
      (snap) => {
        const cats = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        cats.sort((a, b) => (a.order || 0) - (b.order || 0));
        setCategories(cats);
      }
    );
    return () => unsubscribe();
  }, []);

  /* ─── Firestore: Subjects ────────────────────────────────────────────── */
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, "subjects")),
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          name: d.data().name,
          rootSubject: d.data().rootSubject || "Uncategorized",
          tutorIds: d.data().tutorIds || [],
          order: d.data().order || 0,
          ...d.data(),
        }));
        setSubjects(list);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  /* ─── Auto-sync missing categories ──────────────────────────────────── */
  useEffect(() => {
    if (loading || syncInFlightRef.current) return;
    const run = async () => {
      syncInFlightRef.current = true;
      try {
        const needed = new Map();
        subjects.forEach((s) => {
          const raw = s.rootSubject || "Uncategorized";
          const id = normalizeCategoryId(raw);
          if (id) needed.set(id, raw);
        });
        const existing = new Set(categories.map((c) => c.id));
        let max = categories.length > 0 ? Math.max(...categories.map((c) => c.order || 0)) : 0;
        for (const [id, name] of needed.entries()) {
          if (!existing.has(id)) {
            const ref = doc(db, "subject_categories", id);
            const snap = await getDoc(ref);
            if (!snap.exists()) {
              max++;
              await setDoc(ref, { name, order: max });
            }
          }
        }
      } finally {
        syncInFlightRef.current = false;
      }
    };
    run();
  }, [subjects, categories, loading]);

  /* ─── Toggle qualification ───────────────────────────────────────────── */
  const toggleAssignment = async (subject, tutor) => {
    const assigned =
      subject.tutorIds?.includes(tutor.uid) || tutor.subjects?.includes(subject.name);
    const subRef = doc(db, "subjects", subject.id);
    const tutorRef = doc(db, "users", tutor.uid);
    try {
      if (assigned) {
        await updateDoc(subRef, { tutorIds: arrayRemove(tutor.uid) });
        await updateDoc(tutorRef, { subjects: arrayRemove(subject.name) });
        setTutors((prev) =>
          prev.map((t) =>
            t.uid === tutor.uid
              ? { ...t, subjects: t.subjects.filter((s) => s !== subject.name) }
              : t
          )
        );
      } else {
        await updateDoc(subRef, { tutorIds: arrayUnion(tutor.uid) });
        await updateDoc(tutorRef, { subjects: arrayUnion(subject.name) });
        setTutors((prev) =>
          prev.map((t) =>
            t.uid === tutor.uid
              ? { ...t, subjects: [...(t.subjects || []), subject.name] }
              : t
          )
        );
      }
    } catch (err) {
      console.error("Error toggling:", err);
    }
  };

  /* ─── Category CRUD ──────────────────────────────────────────────────── */
  const handleAddCategory = async () => {
    if (!newRootName.trim()) return;
    const max = categories.length > 0 ? Math.max(...categories.map((c) => c.order || 0)) : 0;
    const id = normalizeCategoryId(newRootName);
    await setDoc(
      doc(db, "subject_categories", id),
      { name: newRootName.trim(), order: max + 1 },
      { merge: true }
    );
    setNewRootName("");
    setShowAddRootModal(false);
  };

  const moveCategory = async (idx, dir) => {
    if (idx < 0 || (dir === -1 && idx === 0) || (dir === 1 && idx === categories.length - 1))
      return;
    const arr = [...categories];
    const [moved] = arr.splice(idx, 1);
    arr.splice(idx + dir, 0, moved);
    const batch = writeBatch(db);
    arr.forEach((c, i) => {
      if (c.order !== i + 1) batch.update(doc(db, "subject_categories", c.id), { order: i + 1 });
    });
    await batch.commit();
  };

  const deleteCategory = async (cat) => {
    if (!cat) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete category "${cat.name}"?\n\nSubjects inside will NOT be deleted.`)) return;
    await deleteDoc(doc(db, "subject_categories", cat.id));
  };

  /* ─── Subject CRUD ───────────────────────────────────────────────────── */
  const openAddSubjectModal = (rootName) => {
    setAddSubjectRoot(rootName);
    setNewSubjectName("");
    setShowAddSubjectModal(true);
  };

  const handleCreateSubject = async () => {
    if (!newSubjectName.trim()) return;
    const siblings = subjects.filter((s) => s.rootSubject === addSubjectRoot);
    const max = siblings.length > 0 ? Math.max(...siblings.map((s) => s.order || 0)) : 0;
    await addDoc(collection(db, "subjects"), {
      name: newSubjectName.trim(),
      rootSubject: addSubjectRoot,
      tutorIds: [],
      order: max + 1,
      createdAt: serverTimestamp(),
    });
    setShowAddSubjectModal(false);
  };

  const handleRenameSubject = async (e, subject) => {
    if (e.key === "Enter") {
      if (!tempName.trim()) return setEditingId(null);
      await updateDoc(doc(db, "subjects", subject.id), { name: tempName.trim() });
      setEditingId(null);
    } else if (e.key === "Escape") {
      setEditingId(null);
    }
  };

  const deleteSubject = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this subject?")) return;
    await deleteDoc(doc(db, "subjects", id));
  };

  const moveSubject = async (subject, dir) => {
    if (searchTerm) {
      // eslint-disable-next-line no-alert
      alert("Clear search before reordering.");
      return;
    }
    const siblings = subjects
      .filter((s) => s.rootSubject === subject.rootSubject)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const i = siblings.findIndex((s) => s.id === subject.id);
    if ((dir === -1 && i === 0) || (dir === 1 && i === siblings.length - 1)) return;
    const arr = [...siblings];
    const [moved] = arr.splice(i, 1);
    arr.splice(i + dir, 0, moved);
    const batch = writeBatch(db);
    arr.forEach((s, idx) => {
      if (s.order !== idx + 1) batch.update(doc(db, "subjects", s.id), { order: idx + 1 });
    });
    await batch.commit();
  };

  /* ─── UI toggles ─────────────────────────────────────────────────────── */
  const toggleCategory = (name) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleSubjectExpand = (id) => {
    setExpandedSubjects((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* ─── Data processing ────────────────────────────────────────────────── */
  const { filteredTutors, groupedSubjects } = useMemo(() => {
    let showTutors = tutors.filter(
      (t) => statusFilter === "all" || t.status === statusFilter
    );
    const lower = searchTerm.toLowerCase();
    let visibleSubjects = subjects;

    if (searchTerm) {
      const matchedTutors = showTutors.filter((t) =>
        t.name.toLowerCase().includes(lower)
      );
      if (matchedTutors.length > 0) {
        showTutors = matchedTutors;
        const ids = new Set(matchedTutors.map((t) => t.uid));
        visibleSubjects = subjects.filter(
          (s) =>
            s.tutorIds?.some((id) => ids.has(id)) ||
            matchedTutors.some((t) => t.subjects?.includes(s.name))
        );
      } else {
        visibleSubjects = subjects.filter((s) =>
          (s.name || "").toLowerCase().includes(lower)
        );
        showTutors = showTutors.filter((t) =>
          visibleSubjects.some(
            (s) => s.tutorIds?.includes(t.uid) || t.subjects?.includes(s.name)
          )
        );
      }
    }

    if (tutorFilter) {
      const sel = tutors.find((t) => t.uid === tutorFilter);
      if (sel) {
        visibleSubjects = visibleSubjects.filter(
          (s) => s.tutorIds?.includes(tutorFilter) || sel.subjects?.includes(s.name)
        );
      }
    }

    const data = {};
    categories.forEach((c) => { data[c.name] = []; });
    if (!data["Uncategorized"]) data["Uncategorized"] = [];
    visibleSubjects.forEach((s) => {
      const root = s.rootSubject || "Uncategorized";
      if (!data[root]) data[root] = [];
      data[root].push(s);
    });
    Object.keys(data).forEach((k) => {
      data[k].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    const keys = Array.from(new Set(categories.map((c) => c.name)));
    Object.keys(data).forEach((k) => { if (!keys.includes(k)) keys.push(k); });

    return { filteredTutors: showTutors, groupedSubjects: { keys, data } };
  }, [searchTerm, statusFilter, tutorFilter, subjects, tutors, categories]);

  /* ─── Loading ─────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="sm-page">
        <div className="sm-loading">
          <div className="sm-spinner" />
          <p className="sm-loading-text">Loading subjects…</p>
        </div>
      </div>
    );
  }

  const isFiltered = !!searchTerm || !!tutorFilter;
  const activeTutors = tutors.filter(
    (t) => statusFilter === "all" || t.status === statusFilter
  );

  /* ─── Render ──────────────────────────────────────────────────────────── */
  return (
    <div className="sm-page">

      {/* ── Controls ── */}
      <div className="sm-controls">
        <div className="sm-controls-left">
          <div className="sm-search-wrap">
            <FiSearch className="sm-search-icon" size={14} />
            <input
              ref={searchInputRef}
              className="sm-search-input"
              placeholder="Search subjects or tutor names…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="sm-clear-btn" type="button" onClick={() => setSearchTerm("")}>
                <FiX size={13} />
              </button>
            )}
          </div>

          <select
            className="sm-select"
            value={tutorFilter}
            onChange={(e) => setTutorFilter(e.target.value)}
            aria-label="Filter by tutor"
          >
            <option value="">All Tutors</option>
            {activeTutors.map((t) => (
              <option key={t.uid} value={t.uid}>{t.name}</option>
            ))}
          </select>

          <select
            className="sm-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setTutorFilter(""); }}
            aria-label="Filter by status"
          >
            <option value="active">Active</option>
            <option value="on hold">On Hold</option>
            <option value="finished">Finished</option>
            <option value="all">All Status</option>
          </select>
        </div>

        <div className="sm-controls-right">
          {isEditMode && (
            <button className="sm-btn sm-btn--ghost" onClick={() => setShowAddRootModal(true)}>
              <FiPlus size={13} /> New Category
            </button>
          )}
          <button
            className={`sm-btn ${isEditMode ? "sm-btn--outline" : "sm-btn--primary"}`}
            onClick={() => { setIsEditMode((v) => !v); setExpandedSubjects(new Set()); }}
          >
            {isEditMode ? "Done" : "Edit"}
          </button>
        </div>
      </div>

      {/* ── Category list ── */}
      <div className="sm-content">

        {tutorFilter && (
          <div className="sm-filter-banner">
            Showing subjects for <strong>{activeTutors.find(t => t.uid === tutorFilter)?.name}</strong>
            <button className="sm-filter-clear" onClick={() => setTutorFilter("")}>
              <FiX size={12} /> Clear
            </button>
          </div>
        )}

        {groupedSubjects.keys.map((rootKey) => {
          const catSubjects = groupedSubjects.data[rootKey] || [];
          if (!isEditMode && catSubjects.length === 0) return null;

          const categoryIndex = categories.findIndex((c) => c.name === rootKey);
          const category = categoryIndex >= 0 ? categories[categoryIndex] : null;

          // Auto-open when filtered/searching, otherwise use openCategories
          const isOpen = isFiltered ? catSubjects.length > 0 : openCategories.has(rootKey);

          // Count unique tutors in this category
          const catTutorIds = new Set();
          catSubjects.forEach((s) => {
            filteredTutors.forEach((t) => {
              if (s.tutorIds?.includes(t.uid) || t.subjects?.includes(s.name)) {
                catTutorIds.add(t.uid);
              }
            });
          });

          return (
            <div key={rootKey} className="sm-category">

              {/* Category header */}
              <div
                className={`sm-cat-header ${isOpen ? "sm-cat-header--open" : ""}`}
                onClick={() => !isFiltered && toggleCategory(rootKey)}
                role="button"
                aria-expanded={isOpen}
                style={{ cursor: isFiltered ? "default" : "pointer" }}
              >
                <div className="sm-cat-left">
                  <span className={`sm-cat-chevron ${isOpen ? "sm-cat-chevron--open" : ""}`}>
                    <FiChevronRight size={15} />
                  </span>
                  <span className="sm-cat-name">{rootKey}</span>
                  <span className="sm-cat-badge">{catSubjects.length} subjects</span>
                  {catTutorIds.size > 0 && (
                    <span className="sm-cat-tutor-count">{catTutorIds.size} tutors</span>
                  )}
                </div>
                {isEditMode && (
                  <div className="sm-cat-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="sm-btn sm-btn--ghost sm-btn--xs"
                      onClick={() => openAddSubjectModal(rootKey)}
                    >
                      <FiPlus size={12} /> Add
                    </button>
                    <button
                      className="sm-icon-btn"
                      disabled={categoryIndex <= 0}
                      onClick={() => moveCategory(categoryIndex, -1)}
                      title="Move up"
                    >
                      <FiArrowUp size={12} />
                    </button>
                    <button
                      className="sm-icon-btn"
                      disabled={categoryIndex >= categories.length - 1}
                      onClick={() => moveCategory(categoryIndex, 1)}
                      title="Move down"
                    >
                      <FiArrowDown size={12} />
                    </button>
                    <button
                      className="sm-icon-btn sm-icon-btn--danger"
                      onClick={() => deleteCategory(category)}
                      title="Delete category"
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                )}
              </div>

              {/* Subject list */}
              {isOpen && (
                <div className="sm-subject-list">
                  {catSubjects.length === 0 && isEditMode && (
                    <div className="sm-empty-cat">No subjects yet — click Add above.</div>
                  )}

                  {catSubjects.map((subject) => {
                    const qualTutors = filteredTutors.filter(
                      (t) =>
                        subject.tutorIds?.includes(t.uid) || t.subjects?.includes(subject.name)
                    );
                    const isExpanded = expandedSubjects.has(subject.id);

                    return (
                      <div key={subject.id} className="sm-subject-item">

                        {/* Subject name row */}
                        <div className="sm-subject-row">
                          <div className="sm-subject-left">
                            {isEditMode && (
                              <div className="sm-order-btns">
                                <button
                                  className="sm-icon-btn sm-icon-btn--xs"
                                  onClick={() => moveSubject(subject, -1)}
                                  disabled={!!searchTerm}
                                >
                                  <FiArrowUp size={10} />
                                </button>
                                <button
                                  className="sm-icon-btn sm-icon-btn--xs"
                                  onClick={() => moveSubject(subject, 1)}
                                  disabled={!!searchTerm}
                                >
                                  <FiArrowDown size={10} />
                                </button>
                              </div>
                            )}

                            {editingId === subject.id ? (
                              <input
                                autoFocus
                                className="sm-subject-input"
                                value={tempName}
                                onChange={(e) => setTempName(e.target.value)}
                                onKeyDown={(e) => handleRenameSubject(e, subject)}
                                onBlur={() => setEditingId(null)}
                              />
                            ) : (
                              <span
                                className="sm-subject-name"
                                onDoubleClick={() => {
                                  if (isEditMode) { setEditingId(subject.id); setTempName(subject.name); }
                                }}
                                title={isEditMode ? "Double-click to rename" : ""}
                              >
                                {subject.name}
                              </span>
                            )}
                          </div>

                          {isEditMode && (
                            <div className="sm-subject-actions">
                              <button
                                className={`sm-edit-tutors-btn ${isExpanded ? "sm-edit-tutors-btn--active" : ""}`}
                                onClick={() => toggleSubjectExpand(subject.id)}
                              >
                                <FiEdit2 size={12} />
                                {isExpanded ? "Close" : "Edit Tutors"}
                              </button>
                              <button
                                className="sm-icon-btn sm-icon-btn--danger"
                                onClick={() => deleteSubject(subject.id)}
                                title="Delete"
                              >
                                <FiTrash2 size={13} />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Tutor chips — view mode, ALL tutors shown, wrapping */}
                        {!isEditMode && (
                          <div className="sm-tutor-chips-row">
                            {qualTutors.length === 0 ? (
                              <span className="sm-no-tutors">No tutors assigned</span>
                            ) : (
                              qualTutors.map((t) => (
                                <span key={t.uid} className="sm-tutor-chip">
                                  {t.name}
                                </span>
                              ))
                            )}
                          </div>
                        )}

                        {/* Edit mode: tutor checklist (expanded) */}
                        {isEditMode && isExpanded && (
                          <div className="sm-tutor-panel">
                            <p className="sm-panel-label">
                              {qualTutors.length > 0
                                ? `${qualTutors.length} tutor${qualTutors.length > 1 ? "s" : ""} assigned`
                                : "No tutors assigned"}
                            </p>
                            <div className="sm-tutor-checklist">
                              {filteredTutors.map((t) => {
                                const assigned =
                                  subject.tutorIds?.includes(t.uid) ||
                                  t.subjects?.includes(subject.name);
                                return (
                                  <label
                                    key={t.uid}
                                    className={`sm-tutor-check ${assigned ? "sm-tutor-check--on" : ""}`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="sm-tutor-check-input"
                                      checked={!!assigned}
                                      onChange={() => toggleAssignment(subject, t)}
                                    />
                                    <span className="sm-tutor-check-name">{t.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Empty state */}
        {groupedSubjects.keys.every((k) => (groupedSubjects.data[k] || []).length === 0) && (
          <div className="sm-empty-state">
            <p className="sm-empty-title">No subjects found</p>
            <p className="sm-empty-desc">
              {isFiltered ? "Try adjusting your search or filter." : "Add a category to get started."}
            </p>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showAddRootModal && (
        <div className="sm-modal-overlay" role="dialog" aria-modal="true">
          <div className="sm-modal">
            <div className="sm-modal-header">
              <h2 className="sm-modal-title">New Category</h2>
              <button className="sm-icon-btn" onClick={() => setShowAddRootModal(false)}>
                <FiX size={15} />
              </button>
            </div>
            <div className="sm-modal-body">
              <label className="sm-modal-label">Category name</label>
              <input
                autoFocus
                className="sm-modal-input"
                placeholder="e.g. Math, Science, Languages"
                value={newRootName}
                onChange={(e) => setNewRootName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
              />
              <p className="sm-modal-hint">Categories organize your subject list.</p>
            </div>
            <div className="sm-modal-footer">
              <button className="sm-btn sm-btn--ghost" onClick={() => setShowAddRootModal(false)}>Cancel</button>
              <button className="sm-btn sm-btn--primary" onClick={handleAddCategory}>Create</button>
            </div>
          </div>
        </div>
      )}

      {showAddSubjectModal && (
        <div className="sm-modal-overlay" role="dialog" aria-modal="true">
          <div className="sm-modal">
            <div className="sm-modal-header">
              <h2 className="sm-modal-title">Add Subject</h2>
              <button className="sm-icon-btn" onClick={() => setShowAddSubjectModal(false)}>
                <FiX size={15} />
              </button>
            </div>
            <div className="sm-modal-body">
              <p className="sm-modal-category-tag">Category: <strong>{addSubjectRoot}</strong></p>
              <label className="sm-modal-label">Subject name</label>
              <input
                autoFocus
                className="sm-modal-input"
                placeholder="e.g. Algebra 1, AP Chemistry"
                value={newSubjectName}
                onChange={(e) => setNewSubjectName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateSubject()}
              />
            </div>
            <div className="sm-modal-footer">
              <button className="sm-btn sm-btn--ghost" onClick={() => setShowAddSubjectModal(false)}>Cancel</button>
              <button className="sm-btn sm-btn--primary" onClick={handleCreateSubject}>Add Subject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
